import {
  Check,
  CircleAlert,
  Leaf,
  Loader2,
  PackageCheck,
  ShieldCheck,
  ShoppingBag,
  Truck,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "wouter";
import { PageMeta } from "@/components/PageMeta";
import { useCart } from "@/contexts/CartContext";
import { AMOK_PASTE } from "@/lib/amokPaste";
import { SITE, trackEvent } from "@/lib/site";
import { trpc } from "@/lib/trpc";

const gallery = [
  {
    src: SITE.images.curryPasteSingle640,
    srcSet: `${SITE.images.curryPasteSingle320} 320w, ${SITE.images.curryPasteSingle640} 640w, ${SITE.images.curryPasteSingle960} 960w`,
    thumbnail: SITE.images.curryPasteSingle128,
    alt: "Single 280 g jar of ANGKOR Amok Curry Paste",
  },
  {
    src: SITE.images.curryPasteLifestyle640,
    srcSet: `${SITE.images.curryPasteLifestyle320} 320w, ${SITE.images.curryPasteLifestyle640} 640w, ${SITE.images.curryPasteLifestyle960} 960w`,
    thumbnail: SITE.images.curryPasteLifestyle128,
    alt: "ANGKOR Amok Curry Paste in a Cambodian-inspired kitchen setting",
  },
  {
    src: SITE.images.curryPasteServing640,
    srcSet: `${SITE.images.curryPasteServing320} 320w, ${SITE.images.curryPasteServing640} 640w, ${SITE.images.curryPasteServing960} 960w`,
    thumbnail: SITE.images.curryPasteServing128,
    alt: "Fish Amok serving suggestion made with ANGKOR Amok Curry Paste",
  },
] as const;

function formatAud(value: number) {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 2,
  }).format(value);
}

function ProductSchema({ availableForSale }: { availableForSale?: boolean }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "@id": `https://www.angkorflavours.com.au${AMOK_PASTE.productPath}#product`,
    name: `${AMOK_PASTE.title} ${AMOK_PASTE.jarSize}`,
    url: `https://www.angkorflavours.com.au${AMOK_PASTE.productPath}`,
    description: "280 g jar of Cambodian amok curry paste (kroeung). Galangal, shallots, garlic, turmeric, lemongrass, shrimp (crustacean), kaffir lime, fish sauce (fish), vegetable oil and long red chilli. Contains crustacean and fish. Ships Australia-wide.",
    image: gallery.map(
      image => `https://www.angkorflavours.com.au${image.src}`
    ),
    sku: "ANG-CP-280",
    category: "Curry paste",
    weight: { "@type": "QuantitativeValue", value: 280, unitCode: "GRM" },
    brand: { "@type": "Brand", name: "ANGKOR Cambodian Flavours" },
    offers: {
      "@type": "Offer",
      url: `https://www.angkorflavours.com.au${AMOK_PASTE.productPath}`,
      priceCurrency: "AUD",
      price: "15.00",
      priceSpecification: { "@type": "UnitPriceSpecification", price: "15.00", priceCurrency: "AUD", valueAddedTaxIncluded: true },
      availability: availableForSale === false ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
      itemCondition: "https://schema.org/NewCondition",
      seller: { "@id": "https://www.angkorflavours.com.au/#restaurant" },
      shippingDetails: {
        "@type": "OfferShippingDetails",
        shippingRate: { "@type": "MonetaryAmount", value: "22.75", currency: "AUD" },
        shippingDestination: { "@type": "DefinedRegion", addressCountry: "AU" },
      },
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

function BreadcrumbSchema({ productPage = false }: { productPage?: boolean }) {
  const items = [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://www.angkorflavours.com.au/" },
    { "@type": "ListItem", position: 2, name: "Shop", item: "https://www.angkorflavours.com.au/products" },
    ...(productPage ? [{ "@type": "ListItem", position: 3, name: "Amok Curry Paste 280 g", item: `https://www.angkorflavours.com.au${AMOK_PASTE.productPath}` }] : []),
  ];
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({ "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: items }) }} />;
}

export function ShopPage() {
  return (
    <>
      <PageMeta
        title="Cambodian Amok Curry Paste Shop | ANGKOR Melbourne"
        description="Shop ANGKOR Amok Curry Paste: a 280 g jar of fragrant Cambodian kroeung, $15 including GST, shipped Australia-wide."
        path={AMOK_PASTE.shopPath}
        image={SITE.images.curryPasteSingle640}
      />
      <BreadcrumbSchema />
      <section className="pantry-detail-hero">
        <div className="container pantry-detail-grid">
          <div>
            <p className="eyebrow">ANGKOR pantry</p>
            <h1>
              Cambodian flavour,
              <br />
              <em>ready for home.</em>
            </h1>
            <p>
              Take home our 280 g Amok Curry Paste: fragrant kroeung made with
              lemongrass, galangal, turmeric and kaffir lime leaves.
            </p>
            <div className="inline-actions">
              <Link
                className="button button-gold"
                href={AMOK_PASTE.productPath}
              >
                Shop the jar <ShoppingBag aria-hidden="true" />
              </Link>
            </div>
          </div>
          <img
            src={SITE.images.curryPasteSingle640}
            srcSet={`${SITE.images.curryPasteSingle320} 320w, ${SITE.images.curryPasteSingle640} 640w, ${SITE.images.curryPasteSingle960} 960w`}
            sizes="(max-width: 900px) 88vw, 44vw"
            alt="Single 280 g jar of ANGKOR Amok Curry Paste"
            width="640"
            height="800"
            fetchPriority="high"
          />
        </div>
      </section>
      <section className="section parchment-section">
        <div className="container pantry-story">
          <div className="pantry-story-image">
            <img
              src={SITE.images.curryPasteServing640}
              srcSet={`${SITE.images.curryPasteServing320} 320w, ${SITE.images.curryPasteServing640} 640w`}
              sizes="(max-width: 900px) 88vw, 43vw"
              alt="Fish Amok serving suggestion made with ANGKOR Amok Curry Paste"
              width="640"
              height="800"
              loading="lazy"
            />
            <span>01</span>
          </div>
          <div>
            <p className="eyebrow dark">Amok Curry Paste · 280 g</p>
            <h2>
              {AMOK_PASTE.priceDisplay} <em>including GST.</em>
            </h2>
            <p>
              {AMOK_PASTE.ingredients} {AMOK_PASTE.allergens}
            </p>
            <p>
              <strong>Ships Australia-wide.</strong>
            </p>
            <Link className="text-link" href={AMOK_PASTE.productPath}>
              View product <ShoppingBag aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}

export function AmokPasteProductPage() {
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantityInput, setQuantityInput] = useState("1");
  const [cartError, setCartError] = useState("");
  const { addItem, loading } = useCart();
  const {
    data: product,
    isLoading,
    error,
  } = trpc.commerce.products.byHandle.useQuery({
    handle: AMOK_PASTE.handle,
  });

  const variant = product?.variants[0];
  const quantity = Number(quantityInput);
  const quantityIsValid =
    Number.isInteger(quantity) &&
    quantity >= 1 &&
    quantity <= AMOK_PASTE.maximumQuantity;
  const unitPrice = Number(variant?.price.amount ?? AMOK_PASTE.price);
  const catalogIsReady =
    Boolean(variant?.availableForSale) &&
    variant?.price.currencyCode === "AUD" &&
    unitPrice === AMOK_PASTE.price;
  const estimatedSubtotal = useMemo(
    () => (quantityIsValid ? unitPrice * quantity : 0),
    [quantity, quantityIsValid, unitPrice]
  );

  async function addToCart() {
    if (!variant || !quantityIsValid || !catalogIsReady) return;
    setCartError("");
    try {
      await addItem(variant.id, quantity);
      trackEvent("curry_paste_add_to_cart", { quantity: String(quantity) });
    } catch {
      setCartError("The cart could not be updated. Please try again.");
    }
  }

  return (
    <>
      <PageMeta
        title="Amok Curry Paste 280g | ANGKOR Cambodian Flavours"
        description="Take ANGKOR flavours home. Our 280g jar of Cambodian kroeung for Fish Amok, $15 incl. GST, shipped Australia-wide."
        path={AMOK_PASTE.productPath}
        image={SITE.images.curryPasteSingle640}
      />
      <ProductSchema availableForSale={variant?.availableForSale} />
      <BreadcrumbSchema productPage />

      <section className="shop-product-hero">
        <div className="container shop-product-grid">
          <div className="shop-gallery">
            <div className="shop-main-image">
              <img
                src={gallery[selectedImage].src}
                srcSet={gallery[selectedImage].srcSet}
                sizes="(max-width: 900px) 88vw, 48vw"
                alt={gallery[selectedImage].alt}
                width="640"
                height="800"
                fetchPriority="high"
              />
            </div>
            <div className="shop-thumbnails" aria-label="Product gallery">
              {gallery.map((image, index) => (
                <button
                  type="button"
                  className={selectedImage === index ? "active" : undefined}
                  aria-label={`View image ${index + 1}: ${image.alt}`}
                  aria-pressed={selectedImage === index}
                  onClick={() => setSelectedImage(index)}
                  key={image.thumbnail}
                >
                  <img
                    src={image.thumbnail}
                    alt=""
                    width="128"
                    height="160"
                    loading="eager"
                  />
                </button>
              ))}
            </div>
          </div>

          <div className="shop-product-copy" id="amok-ordering">
            <p className="eyebrow dark">ANGKOR pantry · made from scratch</p>
            <h1>
              Amok Curry Paste <em>280 g</em>
            </h1>
            <p className="shop-product-lede">
              Fragrant Cambodian kroeung for bringing the warmth of Fish Amok to
              your table at home.
            </p>
            <a className="text-link shop-reel-link" href="#amok-reel">
              Watch the 10-second reel
            </a>

            <div className="shop-verified-facts">
              <span>
                <PackageCheck aria-hidden="true" /> 280 g jar
              </span>
              <span>
                <Check aria-hidden="true" /> {AMOK_PASTE.priceDisplay} including
                GST
              </span>
              <span>
                <Truck aria-hidden="true" /> Ships Australia-wide
              </span>
            </div>

            <div className="shop-price-summary">
              <span>Single price per jar</span>
              <strong>{AMOK_PASTE.priceDisplay}</strong>
              <small>Including GST</small>
            </div>

            <div className="shop-quantity-row">
              <label htmlFor="shop-quantity">
                Quantity
                <input
                  id="shop-quantity"
                  type="number"
                  min="1"
                  max={AMOK_PASTE.maximumQuantity}
                  step="1"
                  value={quantityInput}
                  aria-describedby={
                    !quantityIsValid ? "shop-quantity-error" : undefined
                  }
                  onChange={event => setQuantityInput(event.target.value)}
                />
              </label>
              <div className="shop-price-summary" aria-live="polite">
                <span>Subtotal</span>
                <strong>
                  {quantityIsValid ? formatAud(estimatedSubtotal) : "—"}
                </strong>
                <small>
                  {quantityIsValid
                    ? `${quantity} × ${AMOK_PASTE.priceDisplay}`
                    : "Enter 1–50 jars"}
                </small>
              </div>
            </div>

            {!quantityIsValid && (
              <p
                className="shop-validation"
                id="shop-quantity-error"
                role="alert"
              >
                <CircleAlert aria-hidden="true" /> Enter a whole number from 1
                to {AMOK_PASTE.maximumQuantity}.
              </p>
            )}
            {error && (
              <p className="shop-validation" role="alert">
                <CircleAlert aria-hidden="true" /> The shop is temporarily
                unavailable. Please try again.
              </p>
            )}
            {!isLoading && product && !catalogIsReady && (
              <p className="shop-validation" role="alert">
                <CircleAlert aria-hidden="true" /> Online ordering is
                temporarily unavailable while the catalog is refreshed.
              </p>
            )}
            <button
              type="button"
              className="button button-gold shop-add-cart"
              disabled={
                loading || isLoading || !quantityIsValid || !catalogIsReady
              }
              onClick={() => void addToCart()}
            >
              {loading || isLoading ? (
                <Loader2 className="shop-spin" />
              ) : (
                <ShoppingBag />
              )}
              {loading ? "Updating cart" : "Add to cart"}
            </button>
            {cartError && (
              <p className="shop-validation" role="alert">
                <CircleAlert aria-hidden="true" /> {cartError}
              </p>
            )}
            <p className="shop-commerce-note">
              Australia-wide Parcel Post is {AMOK_PASTE.shippingPriceDisplay}{" "}
              per order. Australia only.
            </p>
          </div>
        </div>
      </section>

      <section className="section shop-reel-section" id="amok-reel" aria-labelledby="amok-reel-heading">
        <div className="container shop-reel-grid">
          <div className="shop-reel-copy">
            <p className="eyebrow dark">From our kitchen to your home</p>
            <h2 id="amok-reel-heading">Taste Cambodia <em>at home.</em></h2>
            <p>
              Watch Amok Curry Paste stirred with coconut milk, then see Fish
              Amok served in a banana leaf. A little inspiration for your table.
            </p>
            <p className="shop-reel-size-note">
              <strong>The jar sold here is 280 g.</strong> Packaging shown in the
              reel differs.
            </p>
            <a className="button button-dark" href="#amok-ordering">
              Shop the 280 g jar <ShoppingBag aria-hidden="true" />
            </a>
          </div>
          <figure className="shop-reel-player">
            <video
              controls
              playsInline
              preload="none"
              poster="/manus-storage/angkor-amok-reel-poster_3a6f4397.webp"
              width="720"
              height="1280"
              aria-label="ANGKOR Amok Curry Paste product reel"
              aria-describedby="amok-reel-caption"
              onPlay={() => trackEvent("amok_reel_play", { placement: "product-page" })}
            >
              <source src="/manus-storage/angkor-amok-reel-web_cf992237.mp4" type="video/mp4" />
              Your browser does not support embedded video.
            </video>
            <figcaption id="amok-reel-caption">
              10-second product reel with music. Serving suggestion.
            </figcaption>
          </figure>
        </div>
      </section>

      <section className="section shop-ingredients-section">
        <div className="container shop-ingredients-grid">
          <div>
            <p className="eyebrow">What’s in the jar</p>
            <h2>
              Fragrant kroeung, <em>ready for your kitchen.</em>
            </h2>
            <p className="shop-ingredients-copy">{AMOK_PASTE.ingredients}</p>
            <p className="shop-allergen-statement">
              <strong>Allergens:</strong> {AMOK_PASTE.allergens}
            </p>
          </div>
          <div className="shop-compliance-card">
            <ShieldCheck aria-hidden="true" />
            <div>
              <p className="eyebrow">Allergy information</p>
              <h3>Please read before ordering</h3>
              <p>{AMOK_PASTE.allergens}</p>
              <p>{AMOK_PASTE.crossContact}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section parchment-section">
        <div className="container shop-fulfilment-grid">
          <article>
            <Truck aria-hidden="true" />
            <span>Delivery</span>
            <h3>Ships Australia-wide</h3>
            <p>
              {AMOK_PASTE.shippingLabel} is a flat{" "}
              {AMOK_PASTE.shippingPriceDisplay} per order.
            </p>
            <strong>Australia only</strong>
          </article>
          <article>
            <Leaf aria-hidden="true" />
            <span>Made by ANGKOR</span>
            <h3>Khmer flavour from our kitchen</h3>
            <p>
              Made with the aromatic roots, herbs and spices listed on this
              page.
            </p>
            <strong>{AMOK_PASTE.jarSize} per jar</strong>
          </article>
        </div>
      </section>

      <section className="section shop-serving-section">
        <div className="container shop-serving-grid">
          <div>
            <p className="eyebrow dark">Serving inspiration</p>
            <h2>
              Bring Fish Amok <em>to your table.</em>
            </h2>
            <p>
              Start with our fragrant paste, then make it your own at home. Come
              hungry for the restaurant version too.
            </p>
            <div className="inline-actions">
              <Link className="button button-dark" href="/fish-amok-melbourne">
                Discover Fish Amok
              </Link>
              <Link className="text-link" href="/menu">
                View restaurant menu
              </Link>
            </div>
          </div>
          <figure>
            <img
              src={SITE.images.curryPasteServing640}
              srcSet={`${SITE.images.curryPasteServing320} 320w, ${SITE.images.curryPasteServing640} 640w, ${SITE.images.curryPasteServing960} 960w`}
              sizes="(max-width: 900px) 88vw, 43vw"
              alt="Fish Amok serving suggestion made with ANGKOR Amok Curry Paste"
              width="640"
              height="800"
              loading="lazy"
            />
            <figcaption>Serving suggestion</figcaption>
          </figure>
        </div>
      </section>
    </>
  );
}
